import {Component, Input, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {OrganizationConstant} from '@models/adminstration/organization/constants/organization-constant';
import {PortalGeneralSetting} from '@models/adminstration/organization/constants/portal-general-setting';
import {OrganizationConstantValue} from '@models/adminstration/organization/constants/organization-constant-value';
import {ConstantsService} from '@services/administration/organization/configuration/constants/constants.service';

@Component({
    selector: 'scitis-admin-organization-constant-modal',
    templateUrl: './constant-modal.component.html',
    styleUrls: ['./constant-modal.component.scss'],
    standalone: false
})
export class ConstantModalComponent implements OnInit {
  DECIMAL_PLACES_MIN: number = 0;
  DECIMAL_PLACES_MAX: number = 9;
  isNewOrganizationConstant: boolean = false;
  constantModalForm: UntypedFormGroup = new UntypedFormGroup({});

  @Input()
  constants: OrganizationConstant[] = new Array<OrganizationConstant>();

  @Input()
  constant: OrganizationConstant = new OrganizationConstant();

  @Input()
  portalSettings: PortalGeneralSetting[] = new Array<PortalGeneralSetting>();
  @Input()
  organizationId: number = -1;

  constructor(private activeModal: NgbActiveModal,
              private constantsService:ConstantsService,
              private fb: UntypedFormBuilder) {
  }


  ngOnInit(): void {
    this.initConstant();
    this.portalSettings = this.filterOutUsedPortalSettings(this.constants, this.portalSettings);
    this.isNewOrganizationConstant = this.constant.tenantConstant.id === -1;
    this.constantModalForm = this.generateFormContents(this.constant);
  }

  private initConstant() {
    if (!this.constant) {
      this.constant = new OrganizationConstant();
    }

  }

  onClose() {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel() {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onSave() {
    if (this.constantModalForm.invalid) {
      return;
    }
    if (this.constantsService.verifyOverlappingIntervals(this.constantModalForm)) {
      return;
    }

    this.activeModal.close(this.parseFormValues(this.organizationId, this.constant.tenantConstant.id, this.constantModalForm));
  }

  private filterOutUsedPortalSettings(constants: OrganizationConstant[],
                                      settings: PortalGeneralSetting[]): PortalGeneralSetting[] {
    const filteredPortalSettings = new Array<PortalGeneralSetting>();
    for (const setting of settings) {
      const settingId: number = setting.id;
      let found: boolean = false;
      for (const constant of constants) {
        if (constant.portalSetting.id === settingId) {
          found = true;
          break;
        }
      }
      if (!found) {
        filteredPortalSettings.push(setting);
      }
    }
    return filteredPortalSettings;
  }

  private generateFormContents(constant: OrganizationConstant): UntypedFormGroup {
    const form = this.fb.group({
      portalSetting: this.fb.control(constant.portalSetting, [Validators.required]),
      unit: this.fb.control(constant.tenantConstant.unit),
      decimalPlaces: this.fb.control(constant.tenantConstant.decimalPlaces, [Validators.min(this.DECIMAL_PLACES_MIN), Validators.max(this.DECIMAL_PLACES_MAX)]),
      editValuesFormArray: this.fb.array([])
    });

    const valuesArray = form.controls.editValuesFormArray as UntypedFormArray;
    const arrayLength = valuesArray.length;

    for (const value of constant.tenantConstant.constantValues) {
      valuesArray.insert(arrayLength, this.generateConstantValueItemFormGroup(value));
    }
    return form;
  }

  private generateConstantValueItemFormGroup(constantValue: OrganizationConstantValue): UntypedFormGroup {
    return this.fb.group({
      value: new UntypedFormControl(constantValue.value, [Validators.required]),
      startTimestamp: new UntypedFormControl(constantValue.startTimestamp, [Validators.required]),
      endTimestamp: new UntypedFormControl(constantValue.endTimestamp, [Validators.required])
    });
  }


  // convenience getter for easy access to form fields
  get f() {
    return this.constantModalForm.controls;
  }

  private parseFormValues(organizationId: number, constantId: number, form: UntypedFormGroup): OrganizationConstant {
    const constant = new OrganizationConstant();
    constant.tenantConstant.parentId = organizationId;
    constant.tenantConstant.id = constantId;
    constant.portalSetting = form.controls.portalSetting.value;
    constant.tenantConstant.settingId = constant.portalSetting.id;

    constant.tenantConstant.unit = form.controls.unit.value;
    constant.tenantConstant.decimalPlaces = form.controls.decimalPlaces.value;
    const valuesArray = form.controls.editValuesFormArray as UntypedFormArray;
    const valuesControls = valuesArray.controls as UntypedFormGroup[];
    for (const valueGroup of valuesControls) {
      const value = new OrganizationConstantValue();
      value.value = valueGroup.controls.value.value;
      value.startTimestamp = valueGroup.controls.startTimestamp.value;
      value.endTimestamp = valueGroup.controls.endTimestamp.value;
      constant.tenantConstant.constantValues.push(value);
    }
    return constant;
  }


}
